import { Link } from "react-router-dom";
import { MessageCircle, ShieldAlert, ClipboardList, ArrowRight } from "lucide-react";
import { theme } from "../../../theme";

type QuickActionCardProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  accentColor: string;
};

const QuickActionCard = ({
  title,
  description,
  icon,
  to,
  accentColor,
}: QuickActionCardProps) => {
  return (
    <Link
      to={to}
      className={`group block ${theme.card.padded} hover:-translate-y-0.5 hover:shadow-lg dark:hover:shadow-black/25 ${theme.anim.transition} relative overflow-hidden`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
        style={{
          backgroundColor: `${accentColor}14`,
          color: accentColor,
          border: `1px solid ${accentColor}20`,
        }}
      >
        {icon}
      </div>
      <h3 className={`text-base font-semibold ${theme.text.heading} mb-1.5 dark:group-hover:text-sy-teal group-hover:text-teal-600 ${theme.anim.colors}`}>
        {title}
      </h3>
      <p className={`text-sm ${theme.text.body} leading-relaxed`}>{description}</p>
      <ArrowRight
        className={`absolute bottom-5 right-5 w-4 h-4 ${theme.text.muted} opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200`}
      />
    </Link>
  );
};

export const HomePage = () => {
  return (
    <div className={theme.layout.containerMd}>

      {/* Header */}
      <div className={`mb-8 ${theme.anim.fadeInUp}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className={`${theme.text.accentLabel} mb-2`}>Concussion Co-Pilot</p>
            <h1 className={`text-3xl font-bold ${theme.text.heading} mb-1.5 tracking-tight`}>
              Welcome back
            </h1>
            <p className={`text-base ${theme.text.body}`}>
              Ready to keep your athletes safe.
            </p>
          </div>
        </div>
      </div>

      {/* Primary action — Sideline Assessment */}
      <div className="mb-5 animate-fadeInUp animation-delay-100">
        <Link
          to="/assessment"
          className={`group flex items-center gap-5 ${theme.card.padded} hover:shadow-lg dark:hover:shadow-black/25 dark:hover:border-sy-red/20 hover:border-red-100 hover:-translate-y-0.5 transition-all duration-200`}
        >
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-sy-red/[0.10] border border-sy-red/[0.18] transition-transform duration-200 group-hover:scale-105">
            <ShieldAlert className="w-7 h-7 text-sy-red" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className={`text-lg font-bold ${theme.text.heading}`}>
                Sideline Assessment
              </h3>
              <span className="text-xs font-bold uppercase tracking-wide text-sy-red dark:bg-sy-red/[0.10] bg-red-50 border border-sy-red/[0.18] px-2 py-0.5 rounded-full flex-shrink-0">
                Use on-field
              </span>
            </div>
            <p className={`text-sm ${theme.text.body}`}>
              Step-by-step CRT6 concussion recognition — tap here when you need it
            </p>
          </div>

          <ArrowRight className={`w-5 h-5 ${theme.text.muted} group-hover:text-sy-red group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0 hidden sm:block`} />
        </Link>
      </div>

      {/* Secondary actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fadeInUp animation-delay-200">
        <QuickActionCard
          title="Chat with Charlie"
          description="Get answers about concussion management, protocols, and guidelines"
          icon={<MessageCircle className="w-5 h-5" />}
          to="/chat"
          accentColor="#4FD1C5"
        />
        <QuickActionCard
          title="Assessment History"
          description="View past sideline assessments, add notes, and track case status"
          icon={<ClipboardList className="w-5 h-5" />}
          to="/assessments"
          accentColor="#4FD1C5"
        />
      </div>
    </div>
  );
};
